import * as Sentry from '@sentry/react';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { usePostAuthResetPasswordMutation } from '@/services';
import { toast } from '@/components/ui/toaster';
import { z } from 'zod';

export const Route = createFileRoute('/_unauthenticated/reset-password')({
  component: ResetPasswordPage,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
});

function ResetPasswordPage() {
  const { token } = useSearch({ from: '/_unauthenticated/reset-password' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resetPassword] = usePostAuthResetPasswordMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid or missing token.');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ body: { token, password } }).unwrap();
      setDone(true);
      toast.success('Password has been reset!');
    } catch (error: unknown) {
      Sentry.captureEvent(error as Error, { originalException: error });
      toast.error('Failed to reset password. Try again or request a new link.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-12">
        <div className="w-full max-w-md space-y-8 text-center">
          <h2 className="text-2xl font-semibold">Password reset successful</h2>
          <p className="text-muted-foreground">You can now log in with your new password.</p>
          <div className="flex flex-col gap-2">
            <Link to="/login" className="hover:underline text-primary">
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-semibold">Reset your password</h1>
          <p className="text-muted-foreground">Enter your new password below.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium">
              New Password
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="confirm" className="block text-sm font-medium">
              Confirm Password
            </label>
            <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
          <div className="text-center">
            <Link to="/forgot-password" className="hover:underline text-primary">
              Request new reset link
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
